/**
 * ContactQuickLogPopover — tiny inline form to log a contact with the
 * clinic owner: channel (preset), optional note, save. Anchored under its
 * trigger on desktop; bottom sheet on mobile (CSS).
 */

import { useRef, useState } from "react";
import {
  ChatText,
  DotsThree,
  Envelope,
  Phone,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { useToast } from "../../../../contexts/ToastContext";
import { useAddNivCrmContactMutation } from "../../../../store/services/nivApi";
import useOutsideClose from "../shared/hooks/useOutsideClose";
import { CONTACT_CHANNEL_LABELS } from "../shared/nivDerived";

export const CHANNEL_OPTIONS = [
  { value: "call", icon: Phone },
  { value: "whatsapp", icon: WhatsappLogo },
  { value: "sms", icon: ChatText },
  { value: "email", icon: Envelope },
  { value: "other", icon: DotsThree },
];

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

function ContactQuickLogPopover({ clinicId, author, presetChannel = "call", onClose, align = "end" }) {
  const ref = useRef(null);
  const { showSuccess, showError } = useToast();
  const [channel, setChannel] = useState(presetChannel);
  const [note, setNote] = useState("");
  const [addContact, { isLoading }] = useAddNivCrmContactMutation();

  useOutsideClose(ref, onClose);

  const handleSave = async () => {
    try {
      await addContact({
        clinicId,
        contact: {
          id: newId(),
          date: new Date().toISOString(),
          channel,
          note: note.trim() || null,
          author,
        },
      }).unwrap();
      showSuccess("הקשר נרשם");
      onClose();
    } catch (error) {
      console.error("[CK-7304] Error logging clinic contact:", {
        errorCode: "CK-7304",
        errorMessage: error?.message,
        clinicId,
        channel,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ברישום הקשר");
    }
  };

  return (
    <div
      ref={ref}
      className={`nivck2-pop nivck2-pop-${align} nivck2-contact-pop`}
      role="dialog"
      aria-label="רישום יצירת קשר"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="nivck2-pop-title">איך יצרת קשר?</div>
      <div className="nivck2-channel-seg" role="radiogroup">
        {CHANNEL_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={channel === opt.value}
            className={`nivck2-channel-btn${channel === opt.value ? " is-active" : ""}`}
            onClick={() => setChannel(opt.value)}
            title={CONTACT_CHANNEL_LABELS[opt.value]}
          >
            <opt.icon size={15} weight={channel === opt.value ? "fill" : "regular"} />
            <span>{CONTACT_CHANNEL_LABELS[opt.value]}</span>
          </button>
        ))}
      </div>
      <input
        type="text"
        className="nivck2-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="הערה קצרה (לא חובה)"
        autoFocus
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") handleSave();
        }}
      />
      <div className="nivck2-pop-actions">
        <button type="button" className="nivck2-btn-ghost" onClick={onClose} disabled={isLoading}>
          ביטול
        </button>
        <button type="button" className="nivck2-btn-primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? "שומר…" : "שמור"}
        </button>
      </div>
    </div>
  );
}

export default ContactQuickLogPopover;
